import { useSearchParams } from 'react-router-dom';

import { SelectOption } from '~/shared/utils/types';
import { safeJsonParseArray } from '~/shared/utils/safeJson';
import { stripAutoscalingFiltersFromApiFilters } from '~/shared/utils/autoscalingFilterUtils';
import { clearAllFilterSearchParams, PROCESSED_FILTERS_PARAM_KEY } from '~/shared/utils/filterUrlParams';

import { apiFilterKey } from '../../../common/serachParamKeys';
import {
  AUTOSCALING_ELIGIBILITY_PARAM_KEY,
  AUTOSCALING_STATE_PARAM_KEY,
  FilterStoredInURL,
  buildAutoscalingApiFilters,
  filterNonSpecificGroups,
  combineFilters,
} from '../utils/filterUtils';
import { getApiFilters } from '../helpers';

function setAutoscalingUrlParams(
  updatedParams: URLSearchParams,
  stateIds: string[],
  eligibilityIds: string[]
) {
  if (stateIds.length > 0) {
    updatedParams.set(AUTOSCALING_STATE_PARAM_KEY, stateIds.join(','));
  } else {
    updatedParams.delete(AUTOSCALING_STATE_PARAM_KEY);
  }

  if (eligibilityIds.length > 0) {
    updatedParams.set(AUTOSCALING_ELIGIBILITY_PARAM_KEY, eligibilityIds.join(','));
  } else {
    updatedParams.delete(AUTOSCALING_ELIGIBILITY_PARAM_KEY);
  }
}

export function useFilterUrlParams() {
  const [_searchParams, setSearchParams] = useSearchParams();

  const applyFilters = (
    pendingAutoscaleState: string[],
    pendingEligibility: string[],
    pendingClusterNameFilters: SelectOption[],
    pendingVolumeIdFilters: SelectOption[],
    pendingVolumeTagFilters: SelectOption[],
    pendingAccountFilters: SelectOption[],
    pendingInstanceFilters: SelectOption[]
  ) => {
    setSearchParams(
      (prevParams) => {
        const updatedParams = new URLSearchParams(prevParams);

        const existingFiltersFromUrl = safeJsonParseArray<FilterStoredInURL>(
          prevParams.get(apiFilterKey)
        );

        const baseApiFilters = filterNonSpecificGroups(existingFiltersFromUrl);
        const autoscalingApiFilters = buildAutoscalingApiFilters(
          pendingAutoscaleState,
          pendingEligibility
        );
        const finalApiFilters = combineFilters(
          baseApiFilters,
          pendingClusterNameFilters,
          pendingVolumeIdFilters,
          pendingVolumeTagFilters,
          pendingAccountFilters,
          pendingInstanceFilters,
          autoscalingApiFilters.filter((f) => f.group === 'autoscalingState'),
          autoscalingApiFilters.filter((f) => f.group === 'supported')
        );

        setAutoscalingUrlParams(updatedParams, pendingAutoscaleState, pendingEligibility);

        if (finalApiFilters.length > 0) {
          updatedParams.set(apiFilterKey, JSON.stringify(finalApiFilters));
          const processedFilters = getApiFilters(finalApiFilters);
          updatedParams.set(PROCESSED_FILTERS_PARAM_KEY, JSON.stringify(processedFilters));
        } else {
          updatedParams.delete(apiFilterKey);
          updatedParams.delete(PROCESSED_FILTERS_PARAM_KEY);
        }

        return updatedParams;
      },
      { replace: true }
    );
  };

  const applyAutoscalingFiltersOnly = (stateIds: string[], eligibilityIds: string[]) => {
    setSearchParams(
      (prevParams) => {
        const updatedParams = new URLSearchParams(prevParams);

        const existingFiltersFromUrl = safeJsonParseArray<FilterStoredInURL>(
          prevParams.get(apiFilterKey)
        );

        const filtersWithoutAutoscaling =
          stripAutoscalingFiltersFromApiFilters(existingFiltersFromUrl);
        const autoscalingApiFilters = buildAutoscalingApiFilters(stateIds, eligibilityIds);
        const finalApiFilters = [
          ...filtersWithoutAutoscaling,
          ...autoscalingApiFilters.filter((f) => f.group === 'autoscalingState'),
          ...autoscalingApiFilters.filter((f) => f.group === 'supported'),
        ];

        setAutoscalingUrlParams(updatedParams, stateIds, eligibilityIds);

        if (finalApiFilters.length > 0) {
          updatedParams.set(apiFilterKey, JSON.stringify(finalApiFilters));
          const processedFilters = getApiFilters(finalApiFilters);
          updatedParams.set(PROCESSED_FILTERS_PARAM_KEY, JSON.stringify(processedFilters));
        } else {
          updatedParams.delete(apiFilterKey);
          updatedParams.delete(PROCESSED_FILTERS_PARAM_KEY);
        }

        return updatedParams;
      },
      { replace: true }
    );
  };

  const resetFilters = () => {
    setSearchParams(
      (prev) => {
        const updatedParams = new URLSearchParams(prev);
        clearAllFilterSearchParams(updatedParams, apiFilterKey, PROCESSED_FILTERS_PARAM_KEY);
        return updatedParams;
      },
      { replace: true }
    );
  };

  return { applyFilters, applyAutoscalingFiltersOnly, resetFilters };
}
